/// <reference path="../../typedef/ios-version.d.ts" />
import * as iosVersion from 'ios-version/current';
import { getDeviceWidth, getWindow } from '../helpers';
import { EDeviceEnvironment, MobileAppPlatform, MobileFeatures, USER_AGENT } from '../const';
import { ISkyDeviceConfig, DeviceInfo } from '../interface';
// eslint-disable-next-line @typescript-eslint/naming-convention
import Cookies from 'js-cookie';
import { UAParser } from 'ua-parser-js';

const SMALL_TABLET_MAX_WIDTH_PX = 1024;

const IS_MOBILE_FORCED = new URLSearchParams(getWindow()?.location.search.slice(1)).get('forceMobile') === 'true';
const IS_SHARE_LESSON = getWindow()?.location.pathname.indexOf('/share/') === 0;
const IS_SMALL_TABLET = getDeviceWidth() <= SMALL_TABLET_MAX_WIDTH_PX;

interface MobileCookieData {
  features?: Record<string, boolean>;
  productPlatform?: string;
  platform?: MobileAppPlatform;
}

export class SkyDeviceService {
  private static IS_MOBILE = SkyDeviceService.initMobile(USER_AGENT);
  private static IS_TABLET = SkyDeviceService.initTablet(USER_AGENT);
  private static IS_PHONE = SkyDeviceService.initPhone(USER_AGENT);
  private static MOBILE_COOKIE_NAME = 'skyeng-app';
  private static config: ISkyDeviceConfig = { iosMinVersion: null };
  private static USER_AGENT = USER_AGENT;

  static init(config: ISkyDeviceConfig): void {
    // eslint-disable-next-line functional/immutable-data
    SkyDeviceService.config = config;
  }

  static initUserAgent(userAgent: string | undefined = undefined): void {
    // eslint-disable-next-line functional/immutable-data
    const parserResult = new UAParser(userAgent).getResult();

    if (!parserResult) {
      return;
    }

    // eslint-disable-next-line functional/immutable-data
    SkyDeviceService.USER_AGENT = parserResult;

    // eslint-disable-next-line functional/immutable-data
    SkyDeviceService.IS_MOBILE = SkyDeviceService.initMobile(parserResult);

    // eslint-disable-next-line functional/immutable-data
    SkyDeviceService.IS_TABLET = SkyDeviceService.initTablet(parserResult);

    // eslint-disable-next-line functional/immutable-data
    SkyDeviceService.IS_PHONE = SkyDeviceService.initPhone(parserResult);
  }

  private static initMobile(parserResult: UAParser.IResult): boolean {
    const IS_MOBILE_UA = parserResult.device.type === 'mobile';
    const IS_TABLET_UA = parserResult.device.type === 'tablet';

    return IS_MOBILE_UA || IS_MOBILE_FORCED || (IS_TABLET_UA && IS_SMALL_TABLET && IS_SHARE_LESSON);
  }

  private static initTablet(parserResult: UAParser.IResult): boolean {
    const IS_TABLET_UA = parserResult.device.type === 'tablet';

    return IS_TABLET_UA && !SkyDeviceService.initMobile(parserResult);
  }

  private static initPhone(parserResult: UAParser.IResult): boolean {
    const IS_MOBILE_UA = parserResult.device.type === 'mobile';

    return IS_MOBILE_UA;
  }

  static isPhone(): boolean {
    return SkyDeviceService.IS_PHONE;
  }

  static isMobile(): boolean {
    // force mobile for views started inside mobile app
    return SkyDeviceService.IS_MOBILE || SkyDeviceService.isMobileApp();
  }

  static isMobileApp(): boolean {
    return SkyDeviceService.hasMobileCookie();
  }

  static isTablet(): boolean {
    return SkyDeviceService.IS_TABLET;
  }

  static isDesktop(): boolean {
    return !SkyDeviceService.isMobile() && !SkyDeviceService.isMobileApp() && !SkyDeviceService.isTablet();
  }

  static getEnvironment(): EDeviceEnvironment {
    return SkyDeviceService.isMobileApp() ? EDeviceEnvironment.mobile : EDeviceEnvironment.browser;
  }

  static isMobileBrowserSupported(): boolean {
    return SkyDeviceService.isIosVersionSupported() && SkyDeviceService.isAndroidVersionSupported();
  }

  static isMobileAppVideoEnabled(): boolean {
    return this.isMobileFeatureActive(MobileFeatures.hasNativeVideo);
  }

  static isTouchDevice(): boolean {
    return 'ontouchstart' in window || !!navigator.maxTouchPoints;
  }

  static isMobileFeatureActive(featureName: MobileFeatures): boolean {
    if (!this.isMobileApp()) {
      return false;
    }
    const cookies = this.getMobileCookie();
    return !!cookies.features?.[featureName];
  }

  static getMobileProductPlatform(): string | null {
    if (!this.isMobileApp()) {
      return null;
    }

    const cookies = this.getMobileCookie();
    return cookies?.productPlatform ?? null;
  }

  static getMobilePlatform(): MobileAppPlatform | null {
    if (!this.isMobileApp()) {
      return null;
    }

    const cookies = this.getMobileCookie();
    return cookies?.platform ?? null;
  }

  static isChrome(): boolean {
    const isChromium = (window as unknown as Window & { chrome?: boolean }).chrome,
      winNav = window.navigator,
      vendorName = winNav.vendor,
      isOpera = winNav.userAgent.includes('OPR'),
      isIEedge = winNav.userAgent.includes('Edg'),
      isIOSChrome = winNav.userAgent.match('CriOS'),
      isYaBrowser = winNav.userAgent.match('YaBrowser');

    if (isIOSChrome) {
      return true;
    } else if (
      isChromium !== null &&
      isChromium !== undefined &&
      vendorName === 'Google Inc.' &&
      !isOpera &&
      !isIEedge &&
      !isYaBrowser
    ) {
      return true;
    } else {
      return false;
    }
  }

  static isOpera(): boolean {
    return !!SkyDeviceService.USER_AGENT.browser.name?.includes('Opera');
  }

  static isFirefox(): boolean {
    return !!window.navigator.userAgent.match('Firefox');
  }

  static isSafari(): boolean {
    return !!SkyDeviceService.USER_AGENT.browser.name?.toLowerCase().includes('safari');
  }

  static isYandex(): boolean {
    return !!SkyDeviceService.USER_AGENT.browser.name?.includes('Yandex');
  }

  static isIos(): boolean {
    return (
      SkyDeviceService.USER_AGENT.browser.name === 'Mobile Safari' ||
      // iPad Safari >13, simulated macOS
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ||
      //  not Safari, but iOS
      SkyDeviceService.USER_AGENT.device.model === 'iPhone' ||
      SkyDeviceService.USER_AGENT.device.model === 'iPad' ||
      SkyDeviceService.USER_AGENT.device.model === 'iPod'
    );
  }

  static isAndroid(): boolean {
    return SkyDeviceService.USER_AGENT.os.name === 'Android';
  }

  static isEdge(): boolean {
    return window.navigator.userAgent.includes('Edge');
  }

  // taken from https://stackoverflow.com/questions/4460205/detect-ipad-iphone-webview-via-javascript
  static isIosWebView(): boolean {
    return (
      SkyDeviceService.isIos() &&
      !(window.navigator as Navigator & { standalone?: boolean }).standalone &&
      !window.navigator.userAgent.toLowerCase().includes('safari')
    );
  }

  // taken from https://codepen.io/gapcode/pen/vEJNZN
  static getIeMajorVersion(): number | null {
    const ua = window.navigator.userAgent;

    // Examples of userAgent strings:
    // IE 10: 'Mozilla/5.0 (compatible; MSIE 10.0; Windows NT 6.2; Trident/6.0)'
    // IE 11: 'Mozilla/5.0 (Windows NT 6.3; Trident/7.0; rv:11.0) like Gecko'
    // Edge 12 (Spartan): 'Mozilla/5.0 (Windows NT 10.0; WOW64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/39.0.2171.71 Safari/537.36 Edge/12.0'
    // Edge 13: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/46.0.2486.0 Safari/537.36 Edge/13.10586'

    const msie = ua.indexOf('MSIE ');
    if (msie > 0) {
      // IE 10 or older => return version number
      return parseInt(ua.substring(msie + 5, ua.indexOf('.', msie)), 10);
    }

    const trident = ua.indexOf('Trident/');
    if (trident > 0) {
      // IE 11 => return version number
      const rv = ua.indexOf('rv:');
      return parseInt(ua.substring(rv + 3, ua.indexOf('.', rv)), 10);
    }

    const edge = ua.indexOf('Edge/');
    if (edge > 0) {
      // Edge (IE 12+) => return version number
      return parseInt(ua.substring(edge + 5, ua.indexOf('.', edge)), 10);
    }

    // other browser
    return null;
  }

  static getGoogleChromeMajorVersion(): number | null {
    if (!this.isChrome()) {
      return null;
    }

    const version = SkyDeviceService.USER_AGENT.browser.version;
    if (version === undefined) {
      return null;
    }

    return parseInt(version, 10);
  }

  static isWindows(): boolean {
    return SkyDeviceService.USER_AGENT.os.name === 'Windows';
  }

  static getMobileCookieName(): string {
    return SkyDeviceService.MOBILE_COOKIE_NAME;
  }

  static getOSName(): string {
    return SkyDeviceService.USER_AGENT.os.name ?? '';
  }

  static getBrowserName(): string {
    return SkyDeviceService.USER_AGENT.browser.name ?? '';
  }

  /**
   * Возвращает информацию об текущем устройстве пользователя
   */
  static getDeviceInfo(): DeviceInfo {
    const { os, browser, device } = SkyDeviceService.USER_AGENT;

    return {
      os: `${os.name} ${os.version}`,
      browser: `${browser.name} ${browser.major}`,
      device_type: device.type || 'desktop',
    };
  }

  private static hasMobileCookie(): boolean {
    try {
      return document.cookie.includes(SkyDeviceService.MOBILE_COOKIE_NAME);
    } catch (_e) {
      return false;
    }
  }

  private static getMobileCookie(): MobileCookieData {
    const cookie = Cookies.get(SkyDeviceService.MOBILE_COOKIE_NAME);
    if (cookie) {
      try {
        return JSON.parse(cookie);
      } catch (exception) {
        // just ignore
      }
    }
    return {};
  }

  private static isIosVersionSupported(): boolean {
    return !iosVersion || !iosVersion.major || iosVersion.major >= SkyDeviceService.config.iosMinVersion!;
  }

  private static isAndroidVersionSupported(): boolean {
    // Webview 30- does not support flex
    return document.createElement('p').style.flex !== undefined;
  }
}
